import React, {ChangeEvent, useRef, useState} from 'react';
import { FiUploadCloud } from 'react-icons/fi';
import { FcDocument } from 'react-icons/fc';
import Loader from './loader';
import { fileToBase64 } from '../../utils/resources.util';
import { IFile } from '../../models/document.model';
import { useDocumentStore } from '../../store';
import toast from 'react-hot-toast';

interface DocumentUploadNewProps {
  id: string;
  uploading?: boolean;
  removing?: boolean;
  documentType?: string;
  fileUploaded?: (file: IFile) => void;
  onFileChange?: (data: { file: File | null; base64: string | null }) => void;
  fileRemoved?: () => void;
  onRemove?: () => void;
  label?: string;
  maxSize?: number;
  acceptedFileTypes?: string;
  type: 'file' | 'document';
  uploadFile?: boolean;
}

const AppFileUpload: React.FC<DocumentUploadNewProps> = ({
  id,
  uploading,
  removing,
  documentType,
  fileUploaded,
  onFileChange,
  fileRemoved,
  onRemove,
  label,
  acceptedFileTypes = '',
  uploadFile = false,
  maxSize = 3,
  type,
  // Default accepted file types
  // "image/*,
  // image/png
  // image/jpeg
  // image/gif
  // application/pdf
  // application/msword
  // application/vnd.openxmlformats-officedocument.wordprocessingml.document
  // application/vnd.ms-excel
  // application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
}) => {
  const { getDocIdAfterUpload, removeDocument } = useDocumentStore();

  const [isImage, setIsImage] = React.useState(documentType?.includes('image'));
  const [doc, setDoc] = React.useState<IFile | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const maxFileSize = maxSize * 1024 * 1024; // in bytes

  const handleChange = async (e: ChangeEvent<HTMLInputElement>)=>{
    const file = e.target.files?.[0];
    if (!file) return;

    if (file && file.size > maxFileSize) {
      toast.error(`File uploaded exceeds max file size of ${maxSize}MB`);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Clear input
      }
      return;
    }
    const base64 = await fileToBase64(file);
    type === 'file' ? setSelectedFile(file) : handleUploadDocument(file, base64)

    if (onFileChange) {
      onFileChange({file, base64});
    }
  }
  const handleUploadDocument = async (file: File, base64: string) => {
    setIsUploading(true);
    let fileId = '';
    if (uploadFile) {
      fileId = await getDocIdAfterUpload({
        base64File: base64,
        fileName: file.name,
        documentType: documentType ?? file.type,
      });
    }

    const uploadedFile: IFile = {
      base64File: base64,
      fileName: file.name,
      fileType: file.type,
      bucketName: '',
      preview: URL.createObjectURL(file),
      fileId: fileId,
    };
    setIsImage(uploadedFile.fileType.includes('image'))

    setDoc(uploadedFile);

    if (fileUploaded) {
      fileUploaded(uploadedFile);
    }
    setIsUploading(false);
  }

  const handleRemove = (event: React.MouseEvent<HTMLButtonElement>, doc: IFile|null) =>{
    event.stopPropagation(); // Prevents click from reaching input
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Clear the file input
    }
    if (onFileChange) {
      onFileChange({ file: null, base64: null });
    }
    if (onRemove) {
      onRemove();
    }
    type === 'document' && doc && handleRemoveDocument(doc);
  }
  const handleRemoveDocument = async (doc: IFile) => {
    setIsRemoving(true);
    if (doc.fileId && fileRemoved) {
      const response = await removeDocument(doc.fileId);
      if (response && response.isSuccess) {
        toast.success('File removed successfully.');
        const uploadedFile: IFile = {
          base64File: '',
          fileName: '',
          fileType: '',
          bucketName: '',
          preview: '',
          fileId: '',
        };
        setDoc(uploadedFile);
        fileRemoved();
      } else {
        toast.error('Could not remove file.');
      }
    }
    setIsRemoving(false);
  }

  return (
    <div>
      <p className="text-xs font-semibold mb-4">{label}</p>
      <div className="border border-gray-200 rounded-xl p-4 w-full shadow-sm bg-white">
        {!doc?.base64File && (
          <div className="flex flex-col items-center justify-center gap-4 py-4">
            <input
              type="file"
              onChange={handleChange}
              className="hidden"
              id={id}
              ref={fileInputRef}
              accept={acceptedFileTypes}
            />
            {uploading || isUploading ? (
              <Loader width="1.2rem" borderTopColor="border-t-primary" />
            ) : (
              <label
                htmlFor={id}
                className="flex flex-col items-center justify-center gap-2 cursor-pointer hover:underline"
              >
                <FiUploadCloud className="text-3xl text-[#475467]" />
                <span className="text-primary text-sm font-semibold">
                  Click to upload{' '}
                  <span className="text-[#667085]">or drag and drop</span>
                </span>
                <span className="text-[#667085] text-xs">
                  PNG, JPG or GIF (max. {maxSize}mb)
                </span>
              </label>
            )}
          </div>
        )}

        {removing || isRemoving ? (
          <div className="flex justify-center items-center py-4">
            <Loader width="1.2rem" borderTopColor="border-t-primary" />
          </div>
        ) : (
          doc?.base64File && (
            <div>
              <p className="text-gray-800 font-semibold text-sm truncate mb-2">
                {doc?.fileName}
              </p>

              {isImage ? (
                <img
                  src={doc?.base64File}
                  alt="Document Preview"
                  className="w-full h-32 object-cover rounded-lg mb-4 min-w-[250px]"
                />
              ) : (
                <div className="lex justify-center items-center w-full min-w-[250px]">
                  <FcDocument className="text-5xl my-[3.3rem] mx-auto" />
                </div>
              )}

              {(onRemove || fileRemoved) && (
                <button
                  onClick={(e)=>handleRemove(e, doc)}
                  className="text-red-600 hover:underline font-medium ml-auto flex justify-end"
                >
                  <span className="text-xs bg-[#FFEEE5] px-2 py-1 rounded-xl">
                    Remove
                  </span>
                </button>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AppFileUpload;
